import { useContext, useMemo } from 'react'

import { CONCERTS_SUB_CATEGORY_IDS } from '@/constants'
import performerContext from '@/context/performer'
import useBadgedProductions from '@/hooks/use-badged-productions'
import { useMlbPerformerOpeningDayDesktopFeature } from '@/optimizely/features/mlb-performer-opening-day-feature/hooks/use-mlb-performer-opening-day-desktop-feature'
import { useMlbPerformerOpeningDayMobileFeature } from '@/optimizely/features/mlb-performer-opening-day-feature/hooks/use-mlb-performer-opening-day-mobile-feature'
import { updateHomeOpenerProduction } from '@/optimizely/features/mlb-performer-opening-day-feature/utils'
import { useSellingFastDealsAvailableConcertPerformerFeature } from '@/optimizely/features/selling-fast-deals-available-concert-performer-feature/hooks'
import { useSellingFastDealsAvailableSportsHomePerformerFeature } from '@/optimizely/features/selling-fast-deals-available-sports-home-performer-feature/hooks'
import { useSellingFastDealsAvailableSportsPerformerFeature } from '@/optimizely/features/selling-fast-deals-available-sports-performer-feature/hooks'
import { useSellingFastDealsAvailableTheatrePerformerFeature } from '@/optimizely/features/selling-fast-deals-available-theatre-performer-feature/hooks'
import { Production, ProductionListSelectorType, ProductionListSelectorWithRowsInfoType } from '@/types/app-types'

import { formatStringDateRange } from '../../component'
import { useShouldOpenProductionInNewTab } from '../should-open-in-new-tab/hook'

export const addProductionProperties = (
    productions: ProductionListSelectorType[],
    dealsAvailableIds: number[],
    sellingFastIds: number[],
    performerSelectors: { name: string }[],
    options: {
        shouldOpenProductionInNewTab?: boolean
        isUsingSellingFastDealsAvailableBadging?: boolean
        isFestival?: boolean
        isMlbPerformerOpeningDayFeatureEnabled?: boolean
        isMlbPerformerOpeningDayMobileFeatureEnabled?: boolean
        isMlbPerformerOpeningDayDesktopFeatureEnabled?: boolean
        onProductionClickCapture?: (production: Production, index: number) => void
    },
) => {
    const {
        shouldOpenProductionInNewTab,
        isFestival,
        isUsingSellingFastDealsAvailableBadging,
        isMlbPerformerOpeningDayFeatureEnabled,
        isMlbPerformerOpeningDayMobileFeatureEnabled,
        onProductionClickCapture,
    } = options

    const updatedProductions: ProductionListSelectorType[] = productions.map((production, index) => {
        if (sellingFastIds.includes(production.id)) {
            production.isSellingFast = isUsingSellingFastDealsAvailableBadging
        }

        if (dealsAvailableIds.includes(production.id)) {
            production.isLowPricesDealsAvailable = isUsingSellingFastDealsAvailableBadging
        }

        if (isFestival) {
            const dateRange = formatStringDateRange(production.name, production.shortLocalDate)
            if (dateRange) {
                production.date = dateRange
            }
        }

        if (onProductionClickCapture) {
            production.onClickCapture = () => {
                onProductionClickCapture(production.analytics.production, index)
            }
        }

        production.openInNewTab = Boolean(shouldOpenProductionInNewTab)

        return production
    })

    /* istanbul ignore next */
    if (isMlbPerformerOpeningDayFeatureEnabled) {
        updateHomeOpenerProduction(
            performerSelectors[0].name,
            isMlbPerformerOpeningDayMobileFeatureEnabled ? updatedProductions : undefined,
            undefined,
        )
    }

    return updatedProductions
}

export default function useUpdateProductionProperties(
    productions: ProductionListSelectorType[],
    subCategoryId?: number,
    onProductionClickCapture?: (production: Production, index: number) => void,
    customSort?: <T extends ProductionListSelectorType[] | ProductionListSelectorWithRowsInfoType[]>(a: T) => T,
) {
    const [isSellingFastDealsAvailableConcertPerformerFeatureEnabled] =
        useSellingFastDealsAvailableConcertPerformerFeature()
    const [isSellingFastDealsAvailableTheatrePerformerFeatureEnabled] =
        useSellingFastDealsAvailableTheatrePerformerFeature()
    const [isSellingFastDealsAvailableSportsPerformerFeatureEnabled] =
        useSellingFastDealsAvailableSportsPerformerFeature()
    const [isSellingFastDealsAvailableSportsHomePerformerFeatureEnabled] =
        useSellingFastDealsAvailableSportsHomePerformerFeature()
    const isUsingSellingFastDealsAvailableBadging =
        isSellingFastDealsAvailableConcertPerformerFeatureEnabled ||
        isSellingFastDealsAvailableTheatrePerformerFeatureEnabled ||
        isSellingFastDealsAvailableSportsPerformerFeatureEnabled ||
        isSellingFastDealsAvailableSportsHomePerformerFeatureEnabled

    const [isMlbPerformerOpeningDayMobileFeatureEnabled] = useMlbPerformerOpeningDayMobileFeature()
    const [isMlbPerformerOpeningDayDesktopFeatureEnabled] = useMlbPerformerOpeningDayDesktopFeature()
    const isMlbPerformerOpeningDayFeatureEnabled =
        isMlbPerformerOpeningDayMobileFeatureEnabled || isMlbPerformerOpeningDayDesktopFeatureEnabled

    const { selectors: performerSelectors } = useContext(performerContext)
    const { sellingFastIds, dealsAvailableIds } = useBadgedProductions()

    const isFestival = subCategoryId === CONCERTS_SUB_CATEGORY_IDS.FESTIVALS

    const shouldOpenProductionInNewTab = useShouldOpenProductionInNewTab()

    const productionsWithProperties = useMemo(() => {
        const productionOptions = {
            shouldOpenProductionInNewTab,
            isUsingSellingFastDealsAvailableBadging,
            isFestival,
            isMlbPerformerOpeningDayFeatureEnabled,
            isMlbPerformerOpeningDayMobileFeatureEnabled,
            isMlbPerformerOpeningDayDesktopFeatureEnabled,
            onProductionClickCapture,
        }
        return addProductionProperties(
            customSort?.(productions) ?? productions ?? [],
            dealsAvailableIds,
            sellingFastIds,
            performerSelectors,
            productionOptions,
        )
    }, [
        shouldOpenProductionInNewTab,
        isUsingSellingFastDealsAvailableBadging,
        isFestival,
        isMlbPerformerOpeningDayFeatureEnabled,
        isMlbPerformerOpeningDayMobileFeatureEnabled,
        isMlbPerformerOpeningDayDesktopFeatureEnabled,
        productions,
        dealsAvailableIds,
        sellingFastIds,
        performerSelectors,
    ])

    return productionsWithProperties
}
